import { useCallback, useRef, useEffect } from 'react';

// Sound frequencies for different notification types
const SOUNDS = {
  newOrder: {
    frequencies: [523.25, 659.25, 783.99], // C5, E5, G5 - pleasant chord
    duration: 150,
    type: 'sine'
  },
  orderReady: {
    frequencies: [783.99, 987.77, 1174.66], // G5, B5, D6 - higher success sound
    duration: 120,
    type: 'sine'
  },
  newTable: {
    frequencies: [440, 554.37, 659.25], // A4, C#5, E5 - warm welcome
    duration: 200,
    type: 'triangle'
  },
  alert: {
    frequencies: [880, 1108.73], // A5, C#6 - attention
    duration: 100,
    type: 'square'
  }
};

export function useNotificationSound() {
  const audioContextRef = useRef(null);
  const isEnabledRef = useRef(true);

  useEffect(() => {
    // Check if sounds are enabled from localStorage
    const soundsEnabled = localStorage.getItem('soundsEnabled');
    isEnabledRef.current = soundsEnabled !== 'false';
  }, []);

  const playSound = useCallback((type = 'newOrder') => {
    if (!isEnabledRef.current) return;

    try {
      // Create or reuse AudioContext
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      const ctx = audioContextRef.current;
      const sound = SOUNDS[type] || SOUNDS.newOrder;
      const now = ctx.currentTime;

      // Play each frequency in the chord
      sound.frequencies.forEach((freq, index) => {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();

        oscillator.type = sound.type;
        oscillator.frequency.setValueAtTime(freq, now);

        // Envelope
        gainNode.gain.setValueAtTime(0, now);
        gainNode.gain.linearRampToValueAtTime(0.3, now + 0.02);
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + sound.duration / 1000);

        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);

        // Slight delay for each note to create arpeggio effect
        oscillator.start(now + index * 0.05);
        oscillator.stop(now + sound.duration / 1000 + index * 0.05);
      });
    } catch (error) {
      console.warn('Could not play notification sound:', error);
    }
  }, []);

  const toggleSounds = useCallback((enabled) => {
    isEnabledRef.current = enabled;
    localStorage.setItem('soundsEnabled', enabled.toString());
  }, []);

  return { playSound, toggleSounds, isEnabled: isEnabledRef.current };
}

export default useNotificationSound;
